import React, { FC, useContext, useMemo } from 'react'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { DndContext, DragEndEvent } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities'
import { Button, List } from 'antd'
import { HolderOutlined } from '@ant-design/icons'
import { CSS } from '@dnd-kit/utilities'

type ListItemContextProps = {
  setActivatorNodeRef?: (element: HTMLElement | null) => void
  listeners?: SyntheticListenerMap
}

const ListItemContext = React.createContext<ListItemContextProps>({})

export const DragListItemHandle: React.FC = () => {
  const {setActivatorNodeRef, listeners} = useContext(ListItemContext)
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined/>}
      style={{cursor: 'move'}}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  )
}

type DraggableListItemProps = {
  id: string
  children: React.ReactNode
  style?: React.CSSProperties
}

export const DraggableListItem: FC<DraggableListItemProps> = ({id, children, style}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({id})

  const itemStyle: React.CSSProperties = {
    ...style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? {position: 'relative', zIndex: 9999} : {}),
  }

  const contextValue = useMemo<ListItemContextProps>(
    () => ({setActivatorNodeRef, listeners}),
    [setActivatorNodeRef, listeners],
  )

  return (
    <ListItemContext.Provider value={contextValue}>
      <div ref={setNodeRef} style={itemStyle} {...attributes}>
        {children}
      </div>
    </ListItemContext.Provider>
  )
}

export type DraggableListProps = {
  items: { key: string }[]
  children: React.ReactNode
  onDragEnd: (event: DragEndEvent) => void
}

export const DraggableList: FC<DraggableListProps> = ({items, children, onDragEnd}) => {
  return (
    <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
      <SortableContext
        items={items.map(it => it.key)}
        strategy={verticalListSortingStrategy}
      >
        {children}
      </SortableContext>
    </DndContext>
  )
}
